"use client"

import { useState, useEffect } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"
import { Send } from "lucide-react"
import { usePostEmailsSendEmail } from "openapi/api/endpoints"
import { toast } from "sonner"
import { useMainSignature } from "@/hooks/use-main-signature"

interface EmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  recipientEmail: string
  recipientName: string
  subject?: string
  contactId: string
}

export function EmailDialog({ open, onOpenChange, recipientEmail, recipientName, subject = "", contactId }: EmailDialogProps) {
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    message: "",
  })
  const { mutateAsync, isPending: isSendingEmail } = usePostEmailsSendEmail()
  const { mainSignature, isMainSignatureLoading } = useMainSignature()

  const appendMainSignature = (content: string) => {
    const body = (content ?? "").replace(/\r\n/g, "\n").trimEnd()
    const signature = (mainSignature ?? "").replace(/\r\n/g, "\n").trim()
    if (!signature) return body
    if (body.endsWith(signature)) return body
    return body ? `${body}\n\n${signature}` : signature
  }
  

  useEffect(() => {
    if (open) {
      setEmailData({
        to: recipientEmail,
        subject: subject,
        message: mainSignature,
      })
    }
  }, [open, recipientEmail, subject, mainSignature])

  const handleSend = async() => {
    // Here you would integrate with your email service
    try{
      await mutateAsync({
        data: {
          first_name: recipientName,
          recipientEmail: emailData.to, 
          subject: emailData.subject, 
          message: appendMainSignature(emailData.message), 
          contact_id: contactId??null
        }
      })
      
      toast.success("Sent!",{
        description: 'Email sent successfully',
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#a3e635'
        }
      })
    }catch(err){
      console.log('Error: ',err);
      toast.error("An error occured!",{
        description: `${err}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    }
    console.log("Sending email:", emailData)
    onOpenChange(false)
  }

  const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={`Send Email to ${recipientName}`}
      footer={
        <>
          <QuietAction size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </QuietAction>
          <PageAction
            tone="primary"
            onClick={handleSend}
            disabled={isSendingEmail || isMainSignatureLoading}
          >
            <Send className="size-[15px]" />
            {isSendingEmail ? "Sending…" : isMainSignatureLoading ? "Loading…" : "Send Email"}
          </PageAction>
        </>
      }
    >
      <FieldShell label="To">
        <input
          id="to"
          value={emailData.to}
          onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
          placeholder="recipient@example.com"
          aria-label="To"
          className={CONTROL}
        />
      </FieldShell>

      <FieldShell label="Subject" className="mt-5">
        <input
          id="subject"
          value={emailData.subject}
          onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
          placeholder="Enter email subject"
          aria-label="Subject"
          className={CONTROL}
        />
      </FieldShell>

      <FieldShell label="Message" className="mt-5">
        <textarea
          id="message"
          value={emailData.message}
          onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
          placeholder="Type your message here..."
          rows={10}
          aria-label="Message"
          className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
        />
      </FieldShell>
    </DialogShell>
  )
}
